import {
  QUESTION_SINGLE,
  QUESTION_MULTIPLE,
  QUESTION_OPEN_ENDED,
  QUESTION_NUMERIC_OPEN_ENDED,
  QUESTION_SLIDER,
  QUESTION_RATING,
  QUESTION_RANKING,
  QUESTION_MATRIX_SINGLE,
  QUESTION_MATRIX_MULTIPLE,
  QUESTION_MATRIX_BIPOLAR,
  QUESTION_MATRIX_OPEN_ENDED,
  QUESTION_MATRIX_NUMERIC_OPEN_ENDED,
  QUESTION_MATRIX_CONSTANT_SUM,
  QUESTION_NPS,
  GROUP_AB_TEST,
  QUESTION_DRILLDOWN,
  QUESTION_SINGLE_PEEQ,
  QUESTION_MULTIPLE_PEEQ,
  QUESTION_OPEN_ENDED_PEEQ,
  QUESTION_NUMERIC_OPEN_ENDED_PEEQ,
} from 'data/nodeTypes';

import { compact, isArray, isNil } from 'lodash';
import { isNumericOpenEndedPeeqQuestionType } from 'utils/nodeTypes';
import { LOGIC_MODES } from 'containers/QuestionnairePage/constants';
import DEMOGRAPHIC_TYPES from 'data/demographics';
import { Matcher, QuestionnaireConditionContext } from 'utils/adl-services';
import {
  basicOptions,
  numericOptions,
  containsOptions,
  containsWordOption,
  equalsOption,
  nullOptions,
  includesOptions,
  VISIBILITY_MATCHER,
  VISIBILITY_LEVEL,
} from './constants';

export { VISIBILITY_MATCHER, VISIBILITY_LEVEL } from './constants';

// Question vs Answer level is chosen in the Visibility submenu, not by matcher.
const VISIBILITY_NOT_DISPLAYED_MATCHERS = [
  Matcher.NotIncludes,
  Matcher.NotIncludesAll,
];

export const isVisibilityMatcher = (matcher) =>
  Object.values(VISIBILITY_MATCHER).includes(matcher);

const ANSWER_LEVEL_VISIBILITY_MATCHERS = [
  Matcher.IncludesAll,
  Matcher.NotIncludesAll,
];

// Unlike comparing value to questionId alone (which can't tell before a
// question is picked), the matcher itself reliably encodes the level from
// the moment it's set - getDefaultMatcherForVisibility already picks the
// right one as soon as Question/Answer is chosen from the menu.
export const isAnswerLevelVisibilityMatcher = (matcher) =>
  ANSWER_LEVEL_VISIBILITY_MATCHERS.includes(matcher);

/**
 * Single source of truth for node- vs answer-level Visibility conditions.
 *
 * Priority:
 * 1. visibilityLevel from the submenu (QUESTION/GROUP/METHOD vs ANSWER)
 * 2. ADL matcher (Includes/NotIncludes vs IncludesAll/NotIncludesAll)
 * 3. Value shape: arrays are answer-level; empty/undefined is node-level;
 *    otherwise value === questionId means node-level
 */
export const isNodeLevelVisibility = ({
  value,
  questionId,
  matcher,
  visibilityLevel,
} = {}) => {
  if (!isNil(visibilityLevel)) {
    return visibilityLevel !== VISIBILITY_LEVEL.ANSWER;
  }

  // UI _is_displayed keys don't encode level - fall through to value.
  if (matcher && !isVisibilityMatcher(matcher)) {
    return !isAnswerLevelVisibilityMatcher(matcher);
  }

  if (isArray(value)) {
    return false;
  }

  if (value == null || value === '') {
    return true;
  }

  return Boolean(questionId) && value === questionId;
};

// Answer level conditions created before multi select stored a single refId.
export const getVisibilityValues = (value) => {
  if (isArray(value)) {
    return value;
  }

  return compact([value]);
};

export const resolveVisibilityMatcherSelection = (
  uiMatcher,
  isNodeLevel = true,
) => {
  const isNotDisplayed = uiMatcher === VISIBILITY_MATCHER.IS_NOT_DISPLAYED;

  if (isNodeLevel) {
    return isNotDisplayed ? Matcher.NotIncludes : Matcher.Includes;
  }

  return isNotDisplayed ? Matcher.NotIncludesAll : Matcher.IncludesAll;
};

export const getVisibilityMatcherKey = (matcher) => {
  if (isVisibilityMatcher(matcher)) {
    return matcher;
  }

  return VISIBILITY_NOT_DISPLAYED_MATCHERS.includes(matcher)
    ? VISIBILITY_MATCHER.IS_NOT_DISPLAYED
    : VISIBILITY_MATCHER.IS_DISPLAYED;
};

// keep in sync with messages
export const getTransformedMatcherForMessage = ({
  matcher,
  isNumeric,
  isMatrix,
  isOpenEnded,
  isVisibilityContext,
}) => {
  if (isVisibilityContext && isVisibilityMatcher(matcher)) {
    return matcher;
  }

  switch (matcher) {
    case Matcher.Equal:
      if (isNumeric) {
        return `${Matcher.Equal}_numeric`;
      }
      if (isMatrix) {
        return `${Matcher.Equal}_matrix`;
      }
      if (isOpenEnded) {
        return `${Matcher.Equal}_phrase`;
      }
      return matcher;
    case Matcher.NotEqual:
      if (isNumeric) {
        return `${Matcher.NotEqual}_numeric`;
      }
      if (isMatrix) {
        return `${Matcher.NotEqual}_matrix`;
      }
      return matcher;
    case Matcher.Includes:
    case Matcher.IncludesAll:
      if (isVisibilityContext) {
        return VISIBILITY_MATCHER.IS_DISPLAYED;
      }

      return matcher;
    case Matcher.NotIncludes:
    case Matcher.NotIncludesAll:
      if (isVisibilityContext) {
        return VISIBILITY_MATCHER.IS_NOT_DISPLAYED;
      }

      return matcher;
    default:
      return matcher;
  }
};

export const isNumericQuestionType = (questionType) =>
  [
    QUESTION_NUMERIC_OPEN_ENDED,
    QUESTION_SLIDER,
    QUESTION_RATING,
    QUESTION_NPS,
  ].includes(questionType);

export const isTextQuestionType = (questionType) =>
  [QUESTION_DRILLDOWN].includes(questionType);

export const isNumericDemographic = (demographic, demographicType) => {
  if (isNumericOpenEndedPeeqQuestionType(demographicType)) {
    return true;
  }
  return [DEMOGRAPHIC_TYPES.AGE].includes(demographic);
};

// "Is not visible" only makes sense when the condition can actually be
// evaluated against knowledge we have. For a question/group referencing
// itself, that isn't the case (see the backend engine), so it's kept in the
// list - visible, for discoverability - but disabled rather than selectable.
export const getMatchersForVisibility = (onlyIsVisibleSelectable = false) => [
  {
    value: VISIBILITY_MATCHER.IS_DISPLAYED,
    key: VISIBILITY_MATCHER.IS_DISPLAYED,
  },
  {
    value: VISIBILITY_MATCHER.IS_NOT_DISPLAYED,
    key: VISIBILITY_MATCHER.IS_NOT_DISPLAYED,
    disabled: onlyIsVisibleSelectable,
  },
];

export const getMatchersForNodeType = (
  questionType,
  logicMode = LOGIC_MODES.PLAIN,
  contextType,
) => {
  if (contextType === QuestionnaireConditionContext.Visibility) {
    return getMatchersForVisibility();
  }

  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_RANKING:
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
    case GROUP_AB_TEST:
      return basicOptions;
    case QUESTION_OPEN_ENDED:
    case QUESTION_MATRIX_OPEN_ENDED: {
      if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
        // return [onlyContainsOption, containsWordOption];
        return [equalsOption, containsWordOption];
      }
      return containsOptions;
    }
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED: {
      return [...numericOptions, ...nullOptions];
    }
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_CONSTANT_SUM:
    case QUESTION_SLIDER:
    case QUESTION_RATING:
    case QUESTION_NPS:
    case QUESTION_MATRIX_BIPOLAR:
      return numericOptions;
    default:
      return basicOptions;
  }
};

export const getMatchersForDemographic = (demographic, demographicType) => {
  if (demographicType) {
    switch (demographicType) {
      case QUESTION_SINGLE_PEEQ:
        return basicOptions;
      case QUESTION_MULTIPLE_PEEQ:
        return includesOptions;
      case QUESTION_OPEN_ENDED_PEEQ:
        return containsOptions;
      case QUESTION_NUMERIC_OPEN_ENDED_PEEQ:
        return numericOptions;
      default:
        return basicOptions;
    }
  }

  switch (demographic) {
    case DEMOGRAPHIC_TYPES.AGE:
      return numericOptions;
    case DEMOGRAPHIC_TYPES.GENDER:
    case DEMOGRAPHIC_TYPES.MARITAL_STATUS:
    case DEMOGRAPHIC_TYPES.PARENTAL:
    case DEMOGRAPHIC_TYPES.EDUCATION:
    case DEMOGRAPHIC_TYPES.EMPLOYMENT:
    case DEMOGRAPHIC_TYPES.CAREER:
    case DEMOGRAPHIC_TYPES.RACE:
    case DEMOGRAPHIC_TYPES.INCOME:
    case DEMOGRAPHIC_TYPES.LANGUAGES:
    case DEMOGRAPHIC_TYPES.EMPLOYEES:
    case DEMOGRAPHIC_TYPES.ROLE:
    case DEMOGRAPHIC_TYPES.MANUFACTURER:
    case DEMOGRAPHIC_TYPES.MOBILE_CARRIER:
    case DEMOGRAPHIC_TYPES.OS_PLATFORM:
      return basicOptions;
    default:
      return basicOptions;
  }
};

export const getMatchersForVariable = () => includesOptions;

export const getDefaultMatcherForQuestionType = (
  questionType,
  logicMode = LOGIC_MODES.PLAIN,
) => {
  switch (questionType) {
    case QUESTION_SINGLE:
    case QUESTION_MULTIPLE:
    case QUESTION_NUMERIC_OPEN_ENDED:
    case QUESTION_RANKING:
    case QUESTION_MATRIX_SINGLE:
    case QUESTION_MATRIX_MULTIPLE:
    case QUESTION_DRILLDOWN:
    case QUESTION_MATRIX_NUMERIC_OPEN_ENDED:
    case QUESTION_MATRIX_CONSTANT_SUM:
      return Matcher.Equal;
    case QUESTION_OPEN_ENDED:
    case QUESTION_MATRIX_OPEN_ENDED: {
      if (logicMode === LOGIC_MODES.DISQUALIFICATION) {
        return Matcher.Equal;
      }
      return Matcher.Contains;
    }
    case QUESTION_SLIDER:
    case QUESTION_RATING:
    case QUESTION_NPS:
    case QUESTION_MATRIX_BIPOLAR: {
      return Matcher.GreaterThan;
    }
    default:
      return Matcher.Equal;
  }
};

export const getDefaultMatcherForConcept = () => Matcher.Equal;
export const getDefaultMatcherForVariable = () => Matcher.Includes;
export const getDefaultMatcherForVisibility = (isNodeLevel = true) =>
  isNodeLevel ? Matcher.Includes : Matcher.IncludesAll;

export const getDefaultMatcherForDemographic = (
  demographic,
  demographicType,
) => {
  if (demographicType) {
    switch (demographicType) {
      case QUESTION_SINGLE_PEEQ:
      case QUESTION_NUMERIC_OPEN_ENDED_PEEQ:
        return Matcher.Equal;
      case QUESTION_MULTIPLE_PEEQ:
        return Matcher.Includes;
      case QUESTION_OPEN_ENDED_PEEQ:
        return Matcher.Contains;
      default:
        return Matcher.Equal;
    }
  }

  switch (demographic) {
    case DEMOGRAPHIC_TYPES.AGE:
    case DEMOGRAPHIC_TYPES.GENDER:
    case DEMOGRAPHIC_TYPES.MARITAL_STATUS:
    case DEMOGRAPHIC_TYPES.PARENTAL:
    case DEMOGRAPHIC_TYPES.EDUCATION:
    case DEMOGRAPHIC_TYPES.EMPLOYMENT:
    case DEMOGRAPHIC_TYPES.CAREER:
    case DEMOGRAPHIC_TYPES.RACE:
    case DEMOGRAPHIC_TYPES.INCOME:
    case DEMOGRAPHIC_TYPES.LANGUAGES:
    case DEMOGRAPHIC_TYPES.EMPLOYEES:
    case DEMOGRAPHIC_TYPES.ROLE:
    case DEMOGRAPHIC_TYPES.MANUFACTURER:
    case DEMOGRAPHIC_TYPES.MOBILE_CARRIER:
    case DEMOGRAPHIC_TYPES.OS_PLATFORM:
      return Matcher.Equal;
    default:
      return Matcher.Equal;
  }
};

export const getDefaultMatcherForQuota = () => Matcher.GreaterThanEqual;
